import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import logo from '../../../assets/images/logo.png';
import { SignUpProvider, useSignUp } from './context/SignUpContext';
import ProgressHeader from './components/ProgressHeader';
import StepAccountCreate from './steps/StepAccountCreate';
import StepAccountType from './steps/StepAccountType';
import StepPersonalInfo from './steps/StepPersonalInfo';
import StepAddress from './steps/StepAddress';
import StepBusinessInfo from './steps/StepBusinessInfo';
import StepBankAccount from './steps/StepBankAccount';
import StepCompanyInfo from './steps/StepCompanyInfo';
import StepReview from './steps/StepReview';
import StepSuccess from './steps/StepSuccess';
import './SignUp.scss';

const SignUpContent = () => {
  const navigate = useNavigate();
  const { invitationLink } = useParams();
  const { currentStep, formData, updateField } = useSignUp();

  useEffect(() => {
    if (invitationLink) {
      updateField('invitationLink', invitationLink);
    }
  }, [invitationLink, updateField]);

  const isPJ = formData.accountType === 'SELLER_PJ';

  // Step mapping considering the dynamic company step for PJ
  const getStepComponent = () => {
    switch (currentStep) {
      case 0:
        return <StepAccountCreate />;
      case 1:
        return <StepAccountType />;
      case 2:
        return <StepPersonalInfo />;
      case 3:
        return <StepAddress />;
      case 4:
        return <StepBusinessInfo />;
      case 5:
        return <StepBankAccount />;
      case 6:
        // For PJ: Company step, For PF: Review step
        return isPJ ? <StepCompanyInfo /> : <StepReview />;
      case 7:
        // For PJ: Review step, For PF: Success step
        return isPJ ? <StepReview /> : <StepSuccess />;
      case 8:
        // Only for PJ: Success step
        return <StepSuccess />;
      default:
        return <StepAccountCreate />;
    }
  };

  // Determine if we should show the progress header
  const showProgress = currentStep > 0 && currentStep < (isPJ ? 8 : 7);

  // Determine if we're on the success step
  const isSuccessStep = (isPJ && currentStep === 8) || (!isPJ && currentStep === 7);

  return (
    <div className="SignUp">
      <div className="form-container">
        {/* Logo */}
        <div className="logo">
          <img src={logo} alt="Logo" />
        </div>

        {/* Progress Header */}
        {showProgress && <ProgressHeader />}

        {/* Step Content */}
        <div className="step-wrapper">
          {getStepComponent()}
        </div>

        {/* Back to Login link (only on first step) */}
        {currentStep === 0 && (
          <div className="back-to-login">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/login')}
            >
              Voltar para o login
            </Button>
          </div>
        )}
      </div>

      {/* Branded Background */}
      <div className="background">
        {!isSuccessStep ? (
          <div className="background-content">
            <span className="background-text">
              {currentStep === 0 && 'Comece sua jornada de pagamentos hoje.'}
              {currentStep === 1 && 'Escolha o tipo de conta ideal para você.'}
              {currentStep === 2 && 'Seus dados estão seguros conosco.'}
              {currentStep === 3 && 'Mais um passo para começar a vender.'}
              {currentStep === 4 && 'Configure seu negócio em minutos.'}
              {currentStep === 5 && 'Receba seus pagamentos com segurança.'}
              {currentStep === 6 && (isPJ ? 'Complete os dados da sua empresa.' : 'Revise e confirme seus dados.')}
              {currentStep === 7 && 'Revise e confirme seus dados.'}
            </span>
            <div className="step-indicator">
              <span>Passo {Math.min(currentStep + 1, isPJ ? 8 : 7)} de {isPJ ? 8 : 7}</span>
            </div>
          </div>
        ) : (
          <div className="background-content success">
            <span className="background-text">
              Bem-vindo ao futuro dos pagamentos!
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const SignUp = () => {
  return (
    <SignUpProvider>
      <SignUpContent />
    </SignUpProvider>
  );
};

export default SignUp;
